import React, {
 useEffect,
 useLayoutEffect,
 useRef,
 useState,
} from 'react'
import ReactDOM from 'react-dom'

export type IQualitySelectOption = {
 id: number | string
 name: string
}

type Props = {
 id?: string
 label?: string
 required?: boolean
 placeholder?: string
 value: string | number | null | undefined
 options: IQualitySelectOption[]
 onChange: (id: string | number) => void
 onBlur?: () => void
 error?: string
 disabled?: boolean
 isLoading?: boolean
 searchable?: boolean
 noOptionsText?: string
 wrapperClassName?: string
 endAdornment?: React.ReactNode
}

const QualityParameterSelect = ({
 id,
 label,
 required = false,
 placeholder = 'Select',
 value,
 options,
 onChange,
 onBlur,
 error,
 disabled = false,
 isLoading = false,
 searchable = true,
 noOptionsText = 'No options',
 wrapperClassName = '',
 endAdornment,
}: Props) => {
 const [isOpen, setIsOpen] = useState(false)
 const [search, setSearch] = useState('')
 const [menuPos, setMenuPos] = useState({
  top: 0,
  left: 0,
  width: 0,
 })

 const triggerRef = useRef<HTMLDivElement>(null)
 const menuRef = useRef<HTMLDivElement>(null)
 const searchInputRef = useRef<HTMLInputElement>(null)

 const selectedOption = options.find(
  (option) => String(option.id) === String(value)
 )

 const filteredOptions =
  searchable && search.trim()
   ? options.filter((option) =>
      option.name
       .toLowerCase()
       .includes(search.trim().toLowerCase())
     )
   : options

 const updateMenuPosition = () => {
  const rect = triggerRef.current?.getBoundingClientRect()
  if (!rect) return
  setMenuPos({
   top: rect.bottom + window.scrollY + 4,
   left: rect.left + window.scrollX,
   width: rect.width,
  })
 }

 useLayoutEffect(() => {
  if (!isOpen) return undefined
  updateMenuPosition()
  const handle = () => updateMenuPosition()
  window.addEventListener('scroll', handle, true)
  window.addEventListener('resize', handle)
  return () => {
   window.removeEventListener('scroll', handle, true)
   window.removeEventListener('resize', handle)
  }
 }, [isOpen])

 useEffect(() => {
  if (!isOpen) return undefined
  const handleClickOutside = (event: MouseEvent) => {
   const target = event.target as Node
   const clickedInsideTriggerOrMenu =
    triggerRef.current?.contains(target) ||
    menuRef.current?.contains(target)
   if (clickedInsideTriggerOrMenu) {
    return
   }
   setIsOpen(false)
   setSearch('')
   onBlur?.()
  }
  document.addEventListener('mousedown', handleClickOutside)
  return () =>
   document.removeEventListener(
    'mousedown',
    handleClickOutside
   )
 }, [isOpen, onBlur])

 useEffect(() => {
  if (!isOpen) return undefined
  const handleKeyDown = (event: KeyboardEvent) => {
   if (event.key === 'Escape') {
    setIsOpen(false)
    setSearch('')
   }
  }
  document.addEventListener('keydown', handleKeyDown)
  return () =>
   document.removeEventListener('keydown', handleKeyDown)
 }, [isOpen])

 useEffect(() => {
  if (isOpen && searchable) {
   searchInputRef.current?.focus()
  }
 }, [isOpen, searchable])

 const handleToggle = () => {
  if (disabled || isLoading) return
  setIsOpen((prev) => !prev)
 }

 const handleSelect = (option: IQualitySelectOption) => {
  onChange(option.id)
  setIsOpen(false)
  setSearch('')
 }

 return (
  <div className={`relative ${wrapperClassName}`}>
   {label && (
    <label
     htmlFor={id}
     className='input-label padding-0 text-[#4E83E4] text-xs !font-medium relative pb-1 block'
    >
     {label}
     {required && (
      <span className='required-field pl-[2px] text-[#CC293F]'>
       *
      </span>
     )}
    </label>
   )}
   <div
    ref={triggerRef}
    id={id}
    role='button'
    aria-haspopup='listbox'
    aria-expanded={isOpen}
    tabIndex={disabled ? -1 : 0}
    onClick={handleToggle}
    onKeyDown={(event) => {
     if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle()
     }
    }}
    className={`flex items-center justify-between gap-2 pl-4 pr-3 py-2 rounded-lg border bg-white select-none ${
     disabled
      ? 'bg-[#F5F8FA] cursor-not-allowed border-[#E6EBF0]'
      : 'cursor-pointer border-[#E6EBF0]'
    } ${error ? '!border-[#CC293F]' : ''}`}
   >
    <span
     className={`text-[16px] font-medium truncate ${
      selectedOption ? 'text-[#33393D]' : 'text-[#80868C]'
     }`}
    >
     {isLoading
      ? 'Loading...'
      : selectedOption?.name ?? placeholder}
    </span>
    {endAdornment ? (
     <div
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      role='presentation'
     >
      {endAdornment}
     </div>
    ) : (
     <svg
      className={`w-4 h-4 flex-shrink-0 text-[#80868C] transition-transform ${
       isOpen ? 'rotate-180' : ''
      }`}
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
     >
      <path
       d='M5 7.5L10 12.5L15 7.5'
       stroke='currentColor'
       strokeWidth='1.5'
       strokeLinecap='round'
       strokeLinejoin='round'
      />
     </svg>
    )}
   </div>
   {error && (
    <p className='error-msg error-cls text-[#CC293F] text-xs mt-1'>
     {error}
    </p>
   )}
   {isOpen &&
    typeof document !== 'undefined' &&
    ReactDOM.createPortal(
     <div
      ref={menuRef}
      style={{
       position: 'absolute',
       top: menuPos.top,
       left: menuPos.left,
       width: menuPos.width,
       zIndex: 99999,
      }}
      className='bg-white rounded-lg border border-[#E6EBF0] shadow-lg overflow-hidden'
     >
      {searchable && (
       <div className='p-2 border-b border-[#E6EBF0]'>
        <input
         ref={searchInputRef}
         type='text'
         value={search}
         onChange={(event) => setSearch(event.target.value)}
         placeholder='Search...'
         className='w-full text-sm px-2 py-1 outline-none border border-[#E6EBF0] rounded'
        />
       </div>
      )}
      <div className='max-h-[220px] overflow-y-auto'>
       {filteredOptions.length === 0 ? (
        <p className='px-4 py-3 text-sm text-[#80868C]'>
         {noOptionsText}
        </p>
       ) : (
        filteredOptions.map((option) => {
         const isSelected =
          String(option.id) === String(value)
         return (
          <button
           key={option.id}
           type='button'
           role='option'
           aria-selected={isSelected}
           onClick={() => handleSelect(option)}
           className='w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-[#33393D] hover:bg-[#F5F8FA]'
          >
           <span
            className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
             isSelected
              ? 'border-[#4E83E4]'
              : 'border-[#CED8E0]'
            }`}
           >
            {isSelected && (
             <span className='w-2 h-2 rounded-full bg-[#4E83E4]' />
            )}
           </span>
           {option.name}
          </button>
         )
        })
       )}
      </div>
     </div>,
     document.body
    )}
  </div>
 )
}

export default QualityParameterSelect
